import React, { useEffect, useState } from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Carousel, { CarouselSlide, CarouselStyles } from "@WhiteCap/Widgets/HomePage/Carousel";
import LazyImage, { LazyImageStyle } from "@insite/mobius/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { css } from "styled-components";
import Link, { LinkProps } from "@insite/mobius/Link";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";

const enum fields {
    carouselItem = "carouselItem",
}

export interface CarouselModel {
    fields: {
        image: string;
        title: string;
        info: string;
        buttonText: string;
        destination: LinkFieldValue;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.carouselItem]: CarouselModel[];
    };
}

export interface HeroBannerStyles {
    carousel?: CarouselStyles;
    container?: InjectableCss;
    boxContainer?: InjectableCss;
    contentContainer?: InjectableCss;
    title?: TypographyProps;
    info?: TypographyProps;
    link?: LinkProps;
    buttonContainer?: InjectableCss;
    slidesButtons?: ButtonProps;
    imageContainer?: InjectableCss;
}

const heroBannerStyle: HeroBannerStyles = {
    container: {
        css: css`
            font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        flex-direction: column-reverse;
                        align-items: center;
                    `,
                    css`
                        display: flex;
                        flex-direction: column-reverse;
                        align-items: center;
                    `,
                    css`
                        display: flex;
                        flex-direction: column-reverse;
                        align-items: center;
                    `,
                ])}
        `,
    },
    boxContainer: {
        css: css`
            position: absolute;
            z-index: 1;
            max-width: 480px;
            max-height: 300px;
            width: 40%;
            height: 100%;
            @media (min-width: 991px) and (max-width: 1999px) {
                top: 7%;
                max-width: 40vw;
            }
            @media (min-width: 1200px) and (max-width: 1280px) {
                top: 10%;
            }
            @media (min-width: 1281px) {
                top: 12%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 90%;
                        position: relative;
                        margin-top: -30px;
                    `,
                    css`
                        width: 90%;
                        position: relative;
                        margin-top: -45px;
                    `,
                    css`
                        width: 90%;
                        position: relative;
                        margin-top: -65px;
                        min-height: 200px;
                    `,
                ])}
        `,
    },
    contentContainer: {
        css: css`
            width: 100%;
            height: 100%;
            padding: 50px 70px;
            color: #ffffff;
            background: #323031;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 26px 24px;
                    `,
                    css`
                        padding: 26px 24px;
                    `,
                    css`
                        padding: 26px 24px;
                    `,
                ])}
        `,
    },
    title: {
        css: css`
            font-size: 24px;
            line-height: 30px;
            font-weight: 700;
            margin: 0 0 10px;
            text-transform: uppercase;

            @media (min-width: 991px) and (max-width: 1280px) {
                font-size: 20px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 20px;
                    `,
                    css`
                        font-size: 20px;
                    `,
                    css`
                        font-size: 20px;
                    `,
                ])}
        `,
    },
    info: {
        css: css`
            font-size: 15px;
            line-height: 23px;
            font-weight: 400;
            margin-bottom: 20px;
        `,
    },
    link: {
        css: css`
            color: #f2b124;
            text-decoration: none;
            font-weight: 700;
            font-size: 15px;
            line-height: 20px;
            align-items: center;
            text-transform: uppercase;
            ${TypographyStyle} {
                color: #f2b124;
                &:hover {
                    color: #f2b124;
                }
            }
            &:focus {
                box-shadow: 0 0 3px 1px #00699d;
                outline: none;
            }
        `,
    },
    buttonContainer: {
        css: css`
            position: absolute;
            display: flex;
            bottom: 0;
            right: 0;
        `,
    },
    slidesButtons: {
        css: css`
            width: 65px;
            height: 65px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff !important;
        `,
    },
    imageContainer: {
        css: css`
            margin-left: 10%;
            ${LazyImageStyle} {
                img {
                    height: 400px;
                }
            }
            @media (max-width: 1199px) {
                ${LazyImageStyle} {
                    img {
                        height: auto;
                    }
                }
            }
            @media (min-width: 991px) and (max-width: 1280px) {
                max-width: 80vw;
                float: right;
            }
            @media (min-width: 1281px) {
                max-width: 1050px;
                float: right;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0;
                    `,
                    css`
                        margin-left: 0;
                    `,
                    css`
                        margin-left: 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const GenrateUrl = ({ text, link }: { text: string; link: LinkFieldValue }) => {
    const url = useGetLink(link);
    return (
        <Link href={url.url} {...styles.link}>
            {text}
        </Link>
    );
};

const styles = heroBannerStyle;
const HeroBanner = ({ fields }: OwnProps) => {
    const [slides, setSlides] = useState<CarouselSlide[]>([]);

    const mapToSlides = () =>
        fields.carouselItem &&
        fields.carouselItem.map(item => ({
            id: Math.random().toString(),
            renderComponent: function ProductCarouselProductCondensedRenderer({ sidePerview, sideNext }: any) {
                return (
                    <StyledWrapper {...styles.container}>
                        <StyledWrapper {...styles.boxContainer}>
                            <StyledWrapper {...styles.contentContainer}>
                                <Typography {...styles.title}>{item.fields.title}</Typography>
                                <Typography as="div" {...styles.info}>
                                    {item.fields.info}
                                </Typography>
                                <GenrateUrl text={item.fields.buttonText} link={item.fields.destination} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.buttonContainer}>
                                <Button onClick={() => sidePerview()} {...styles.slidesButtons}>
                                    ←
                                </Button>
                                <Button onClick={() => sideNext()} {...styles.slidesButtons}>
                                    →
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.imageContainer}>
                            <LazyImage src={item.fields.image}></LazyImage>
                        </StyledWrapper>
                    </StyledWrapper>
                );
            },
        }));

    useEffect(() => {
        setSlides(mapToSlides);
    }, [fields]);
    return <Carousel id="HeroBanner" extendedStyles={styles.carousel} maxNumberOfColumns={1} slides={slides} />;
};

const HeroBannerModule: WidgetModule = {
    component: HeroBanner,
    definition: {
        displayName: "Hero Banner",
        fieldDefinitions: [
            {
                name: fields.carouselItem,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Banner Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },

                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "info",
                        displayName: "Sub title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "buttonText",
                        displayName: "Link Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        displayName: "Link Url",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isRequired: true,
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default HeroBannerModule;
